import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from "../integrations/supabase/client";
import { User, Session } from '@supabase/supabase-js';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        setSession(data.session);
        setUser(data.session?.user ?? null);
      } catch (error) {
        console.error('Error getting initial session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getInitialSession();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    // Cleanup on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      // First, clear all local state and storage before attempting Supabase signout
      // Clear Spotify connection data from localStorage
      localStorage.removeItem('spotify_connected');
      localStorage.removeItem('spotify_connected_at');
      localStorage.removeItem('spotify_user_id');
      
      // Clear all of Supabase's storage as well
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('supabase.auth.expires_at');
      sessionStorage.removeItem('supabase.auth.token');
      
      // Clear guest usage tracking
      localStorage.removeItem('guestToolUsage');
      
      // Manually clear the user and session state
      setUser(null);
      setSession(null);
      
      // Now try the Supabase signout (but we don't depend on its success)
      try {
        await supabase.auth.signOut();
      } catch (error) {
        console.error('Error signing out from Supabase:', error);
        // We continue regardless of this error
      }
      
      // Silent signout - no toast message
      
      // Redirect with a full page refresh to clear any remaining state
      setTimeout(() => {
        window.location.replace('/');
      }, 300);
    } catch (error) {
      console.error('Error in signOut function:', error);
      
      // Still clear local state as a fallback
      setUser(null);
      setSession(null);
      
      // Clear guest usage tracking in case of error
      localStorage.removeItem('guestToolUsage');
      
      localStorage.clear(); // Clear all localStorage as a last resort
      
      // Force a full page refresh to clear React state
      setTimeout(() => {
        window.location.replace('/');
      }, 300);
    }
  };

  const value = {
    user,
    session,
    isLoading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
